import axios, { AxiosRequestConfig, ResponseType } from "axios";
import { createRef } from "react";
import { announceSessionExpiry, resetSessionExpiryNotice } from "./sessionExpiry";
import { readStoredToken } from "./storedSession";
const querystring = require("querystring");
export const tokenRef = createRef();

interface RequestParams {
  url: string;
  data?: any;
  headers?: any;
  responseType?: ResponseType;
  onUploadProgress?: (progressEvent: any) => void;
  // Staff intents must never be replayed automatically with another session's token.
  retryWithStoredToken?: boolean;
  // Send the admin origin's own cookies (the staff device id behind dual control). Opt-in per call: the admin
  // deploy must allow credentialed CORS for this origin first (ADMIN_CORS_CREDENTIALED_ORIGINS).
  withCredentials?: boolean;
}

const sendRequest = {
  post: <T = any>(params: RequestParams) => handleRequest<T>(params, "post"),
  put: <T = any>(params: RequestParams) => handleRequest<T>(params, "put"),
  // The catalogue movie-authoring endpoints are PATCH, not PUT (partial field
  // updates on a draft) - no caller needed the verb before this.
  patch: <T = any>(params: RequestParams) => handleRequest<T>(params, "patch"),
  get: <T = any>(params: RequestParams) => handleRequest<T>(params, "get"),
  delete: <T = any>(params: RequestParams) => handleRequest<T>(params, "delete"),
};

export default sendRequest;

interface ResponseData<T> {
  data: T;
  message: string;
  status: string;
}

function handleRequest<T>(
  params: RequestParams,
  method: "post" | "put" | "patch" | "get" | "delete"
): Promise<ResponseData<T>> {
  return new Promise((resolve, reject) => {
    if (!params.url) {
      reject({ message: "URL is required for http request" });
      return;
    }

    if (params.data && ["get", "delete"].includes(method)) {
      const query = querystring.stringify(params.data);
      if (query) {
        params.url += (params.url.includes("?") ? "&" : "?") + query;
      }
    }
    console.log("---> request: ", params.url);

    const config: AxiosRequestConfig & { _retryWithStoredToken: boolean } = {
      _retryWithStoredToken: params.retryWithStoredToken !== false,
      method,
      responseType: params.responseType,
      url: params.url,
      data: params.data,
      onUploadProgress: params.onUploadProgress,
      ...(params.withCredentials ? { withCredentials: true } : {}),
      headers: {
        ...params.headers,
        // "Content-Type": "application/json",
        Authorization: "Bearer " + tokenRef.current,
      },
    };
    request
      .request(config)
      .then((res) => {
        resolve(res?.data);
      })
      .catch((err) => reject(err));
  });
}

const request = axios.create({
  baseURL: process.env.REACT_APP_CUSTOM_BASE_URL,
});

request.interceptors.response.use(
  async function (config) {
    resetSessionExpiryNotice();
    return config;
  },
  async function (error) {
    const err = error.toJSON();
    if (err?.status === 401) {
      // A refusal is only a session end if this tab already holds the newest
      // token. Another tab of the same browser may have been handed a new one
      // (`auth/session` renews it) - then this request just carried the old
      // one, and signing out here would wipe the shared session for every tab.
      const config = error.config;
      // Caller handles this refusal. Do not replay a privileged intent or let an
      // old request log out the newer account that another tab just selected.
      if (config?._retryWithStoredToken === false) return Promise.reject(error);
      const sentToken = String(config?.headers?.Authorization ?? "").replace(/^Bearer /, "");
      const storedToken = readStoredToken();
      if (config && !config._retriedWithStoredToken && storedToken && storedToken !== sentToken) {
        // @ts-ignore - tokenRef.current is mutable
        tokenRef.current = storedToken;
        config._retriedWithStoredToken = true;
        config.headers.Authorization = "Bearer " + storedToken;
        return request.request(config);
      }

      // Said once for the whole session end, however many requests it refuses.
      announceSessionExpiry();
      // Optional: the boot-time session refresh runs before the router mounts,
      // and the router is what registers this.
      // @ts-ignore
      window.handleLogout?.();
    }
    return Promise.reject(error);
  }
);
